import React, { useEffect, useState } from "react";

import { adminTicketsService } from "api/services/tickets.service";
import ContentHeader from "components/Layout/ContentHeader";
import TicketPurchaseDetailModal from "components/ticket-group/TicketPurchaseDetailModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import { getApiErrorMessage } from "utils/api-error";
import { TICKET_STATUS_BADGE, TICKET_STATUS_OPTIONS } from "utils/constant/ticket";

import type { AdminTicket, TicketStatus } from "api/types/tickets.types";

import "pages/AdminPage.scss";
import "./TicketPurchaseList.scss";

const PAGE_SIZE = 20;

const formatDate = (v: string | null): string => (v ? String(v).slice(0, 10) : "-");

const TicketPurchaseList: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tickets, setTickets] = useState<AdminTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);

  const fetchTickets = async (page: number, status: string, q: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await adminTicketsService.list({
        page,
        page_size: PAGE_SIZE,
        ...(status ? { status: status as TicketStatus } : {}),
        ...(q ? { search: q } : {}),
      });
      setTickets(res.data.results ?? []);
      setTotal(res.data.count ?? 0);
    } catch (err) {
      setError(getApiErrorMessage(err));
      setTickets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets(currentPage, statusFilter, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchTickets(1, statusFilter, search);
  };

  const openModal = (id: number) => {
    setSelectedId(id);
    setShowModal(true);
  };

  const columns: Column<AdminTicket>[] = [
    { key: "user_name", title: "회원명", render: (v) => String(v ?? "-") },
    { key: "user_id", title: "ID" },
    { key: "lesson_product_name", title: "강습 상품" },
    {
      key: "payment_amount",
      title: "결제금액",
      width: "110px",
      render: (v) => (v != null && v !== "" ? `${Number(v).toLocaleString()}원` : "-"),
    },
    { key: "created_at", title: "구매일", width: "110px", render: (v) => formatDate(v) },
    {
      key: "status",
      title: "상태",
      width: "90px",
      render: (v, row) => <Badge variant={TICKET_STATUS_BADGE[v as TicketStatus] ?? "draft"}>{row.status_display}</Badge>,
    },
    {
      key: "sessions_total",
      title: "세션",
      render: (v, row) => `${row.sessions_used}/${v}`,
    },

    { key: "valid_until", title: "만료일", width: "110px", render: (v) => formatDate(v) },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="수강권 구매 목록" description="회원이 구매한 수강권을 조회하고 세션·유효기간을 관리합니다." />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Input placeholder="회원 ID, 회원명, 상품명, 주문번호 검색" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} style={{ width: 280 }} prefix="🔍" />
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[{ value: "", label: "상태 전체" }, ...TICKET_STATUS_OPTIONS]}
            style={{ width: 140 }}
          />
          <Button variant="secondary" size="sm" onClick={handleSearch}>
            조회
          </Button>
        </div>

        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={tickets} rowKey="id" onRowClick={(row) => openModal(row.id)} />}

        <Pagination current={currentPage} total={total} pageSize={PAGE_SIZE} onChange={setCurrentPage} />
      </div>

      <TicketPurchaseDetailModal open={showModal} ticketId={selectedId} onClose={() => setShowModal(false)} onUpdated={() => fetchTickets(currentPage, statusFilter, search)} onDeleted={() => fetchTickets(currentPage, statusFilter, search)} />
    </div>
  );
};

export default TicketPurchaseList;
